import {
  BufferGeometry,
  Uint16BufferAttribute,
  Float32BufferAttribute,
  ShaderMaterial,
  AdditiveBlending,
  Vector2,
  Color,
  Mesh,
  TextureLoader,
  DepthFormat,
  UnsignedShortType,
  DepthTexture,
  NearestFilter,
  RGBAFormat,
  WebGLRenderTarget,
  Vector3,
} from "three";

export class CloudUtil {
  constructor(scene, renderer, camera, el) {
    this.scene = scene;
    this.renderer = renderer;
    this.camera = camera;
    this.el = el;

    this.renderTarget = new WebGLRenderTarget(1, 1);
    this.renderTarget.texture.format = RGBAFormat;
    this.renderTarget.texture.minFilter = NearestFilter;
    this.renderTarget.texture.magFilter = NearestFilter;
    this.renderTarget.texture.generateMipmaps = false;
    this.renderTarget.stencilBuffer = false;
    this.renderTarget.depthBuffer = true;
    this.renderTarget.depthTexture = new DepthTexture();
    this.renderTarget.depthTexture.type = UnsignedShortType;
    this.renderTarget.depthTexture.format = DepthFormat;

    this.clouds = [];
  }

  update(dt) {
    if (this.clouds.length === 0) return;
    for (var i = 0; i < this.clouds.length; i++) {
      this.clouds[i].visible = false;
    }

    const ctx = this.renderer.getContext();

    this.renderer.setRenderTarget(this.renderTarget);

    ctx.colorMask(false, false, false, false);
    this.renderer.render(this.scene, this.camera);

    this.renderer.setRenderTarget(null);

    if (!this.el || !this.el.parentElement) return;
    let { clientHeight, clientWidth } = this.el.parentElement;

    for (var i = 0; i < this.clouds.length; i++) {
      this.clouds[i].visible = true;

      this.clouds[i].material.uniforms.uTime.value = dt;
      this.clouds[i].material.uniforms.uDepthTexture.value =
        this.renderTarget.depthTexture;
      this.clouds[i].material.uniforms.uCameraNear.value = this.camera.near;
      this.clouds[i].material.uniforms.uCameraFar.value = this.camera.far;
      this.clouds[i].material.uniforms.uDepthFade.value = 0.05;
      this.clouds[i].material.uniforms.uResolution.value = new Vector2(
        clientWidth,
        clientHeight
      );
    }

    ctx.colorMask(true, true, true, true);
    this.renderer.render(this.scene, this.camera);
  }

  resize() {
    if (!this.el || !this.el.parentElement) return;
    let { clientHeight, clientWidth } = this.el.parentElement;

    this.renderTarget.setSize(clientHeight, clientWidth);
  }

  async createCloud(
    num = 1000,
    sizeRange = 200,
    sizeMin = 100,
    randomOffsetRange = 5000
  ) {
    if (!this.texture) {
      new TextureLoader().load("/assets/imgs/smoke.png", (tex) => {
        this.texture = tex;
        this._create(num, sizeRange, sizeMin, randomOffsetRange);
      });
    } else {
      this._create(num, sizeRange, sizeMin, randomOffsetRange);
    }
  }

  /**
   * 内部使用
   * @param {*} num
   * @param {*} sizeRange
   * @param {*} sizeMin
   * @param {*} randomOffsetRange
   * @returns
   */
  _create(
    num = 1000,
    sizeRange = 200,
    sizeMin = 100,
    randomOffsetRange = 5000
  ) {
    const vertexShader = `
    attribute float index;
    attribute vec2 offset;
    attribute vec2 size;
    attribute vec3 color;
  
    varying vec2 vUv;
    varying vec4 vViewPosition;
    varying float vFade;
  
    uniform float uTime;
  
    mat2 rotMat(float rad) {
      return mat2(
        cos(rad), sin(rad),
        -sin(rad), cos(rad)
      );
    }
  
    void main() {
      vUv = uv;
  
      vec3 vertexPosition = position;
  
      float moveSpeed = .9;
      float moveAnim = mod((uTime + index * 200.) / moveSpeed, 1.);
      float moveFade = smoothstep(0., .7, moveAnim) * (1. - smoothstep(.3, 1., moveAnim));
  
      vFade = moveFade;
  
      vec3 positionOffset = vec3(
        0,
        0,
        mix(2., -2., moveAnim)
      );
      vertexPosition += positionOffset;
  
      vec4 mvPosition = modelViewMatrix * vec4(vertexPosition, 1.0);
  
      vViewPosition = mvPosition;
  
      mvPosition.xy += offset * vec2(size.x, size.y);
      gl_Position = projectionMatrix * mvPosition;
    }
  `;

    const fragmentShader = `
    precision highp float;
  
    #include <alphatest_pars_fragment>
    #include <packing>
    #include <fog_pars_fragment>
  
    varying vec2 vUv;
    varying vec4 vViewPosition;
    varying float vFade;
  
    uniform float uCameraNear;
    uniform float uCameraFar;
    uniform vec2 uResolution;
    uniform sampler2D uDepthTexture;
    uniform float uDepthFade;
    uniform sampler2D uMaskTexture;
    uniform float uEnableFade;
    uniform vec3 uColor;
  
    float readDepth(sampler2D depthSampler, vec2 coord) {
      float fragCoordZ = texture2D(depthSampler, coord).x;
      float viewZ = perspectiveDepthToViewZ(fragCoordZ, uCameraNear, uCameraFar);
      return viewZToOrthographicDepth(viewZ, uCameraNear, uCameraFar);
    }
  
    void main() {
      vec4 diffuseColor = vec4(vec3(0.), 1.);
  
      float mask = texture2D(uMaskTexture, vUv).r;
  
      diffuseColor = vec4(uColor,0.16);
  
      vec2 screenCoord = gl_FragCoord.xy / uResolution;
  
      float sceneDepth = readDepth(uDepthTexture, screenCoord);
  
      float viewZ = vViewPosition.z;
      float currentDepth = viewZToOrthographicDepth(viewZ, uCameraNear, uCameraFar);
      float depthFade = mix(
        1.,
        clamp(abs(currentDepth - sceneDepth) / max(uDepthFade, .0001), 0., 1.),
        uEnableFade
      );
  
      diffuseColor.a *= vFade * depthFade * mask;
  
      gl_FragColor = diffuseColor;
  
      #include <alphatest_fragment>
      #include <fog_fragment>
    }
  `;
    const geometry = new BufferGeometry();

    // polygon indexes
    // 3 -- 2
    // |    |
    // 0 -- 1
    const index = [];
    const vertices = [];
    const uvs = [];
    const offsets = [];
    const indices = [];
    const sizes = [];
    const colors = [];

    const particleNum = num;

    for (let i = 0; i < particleNum; i++) {
      const px = Math.random() * randomOffsetRange - randomOffsetRange * 0.5;
      const py = -Math.random() * 0.4 - 0.1;
      const pz = Math.random() * randomOffsetRange - randomOffsetRange * 0.5;
      const size = Math.random() * sizeRange + sizeMin;
      const color = {
        x: Math.random() * 0.4 + 0.6,
        y: Math.random() * 0.4 + 0.4,
        z: Math.random() * 0.2 + 0.2,
      };

      for (let j = 0; j < 4; j++) {
        index.push(i);
        vertices.push(px, py, pz);
        sizes.push(size, size);
        colors.push(color.x, color.y, color.z);
      }
      uvs.push(0, 0, 1, 0, 1, 1, 0, 1);
      offsets.push(-1, -1, 1, -1, 1, 1, -1, 1);
      const vertexIndex = i * 4;
      indices.push(
        vertexIndex + 0,
        vertexIndex + 1,
        vertexIndex + 2,
        vertexIndex + 2,
        vertexIndex + 3,
        vertexIndex + 0
      );
    }
    geometry.setIndex(indices);
    geometry.setAttribute("index", new Uint16BufferAttribute(index, 1));
    geometry.setAttribute("position", new Float32BufferAttribute(vertices, 3));
    geometry.setAttribute("uv", new Uint16BufferAttribute(uvs, 2));
    geometry.setAttribute("offset", new Float32BufferAttribute(offsets, 2));
    geometry.setAttribute("size", new Float32BufferAttribute(sizes, 2));
    geometry.setAttribute("color", new Float32BufferAttribute(colors, 3));

    const material = new ShaderMaterial({
      vertexShader: vertexShader,
      fragmentShader: fragmentShader,
      transparent: true,
      blending: AdditiveBlending,
      alphaTest: 0.1,
      depthWrite: false,
      depthTest: true,
      frustumCulled: false, // 新增：禁用视锥体剔除
      uniforms: {
        uMaskTexture: {
          value: this.texture,
        },
        uTime: {
          value: 0,
        },
        uCameraNear: {
          value: 0,
        },
        uCameraFar: {
          value: 0,
        },
        uDepthFade: {
          value: 0,
        },
        uResolution: {
          value: new Vector2(),
        },
        uDepthTexture: {
          value: null,
        },
        uEnableFade: {
          value: 1.0, // 0.0 关闭
        },
        uSpriteGrid: {
          value: new Vector2(4, 4),
        },
        uColor: {
          value: new Vector3(1, 1, 1),
        },
      },
    });

    const cloud = new Mesh(geometry, material);
    cloud.position.y = 200;
    this.clouds.push(cloud);
    this.scene.add(cloud);
  }
}
